import { DynamoDBClient, QueryCommand } from "@aws-sdk/client-dynamodb";
import {
  BedrockRuntimeClient,
  InvokeModelCommand,
} from "@aws-sdk/client-bedrock-runtime";
import { createAwsClient } from "./AwsAuth";
import { extractBetweenTags, removeCharFromStartAndEnd, handleFormatter } from "./Utils.js";
import { postProcessChart, validateChartConsistency } from "./ChartConsistency.js";
import {
  QUESTION_ANSWERS_TABLE_NAME,
  MODEL_ID_FOR_CHART,
  CHART_PROMPT
} from "../env.js";

/**
 * Query data from DynamoDB
 *
 * @param {string} id - The ID to query
 * @returns {Promise<Object>} - The query response
 */
export const getQueryResults = async (queryUuid = "") => {
  let queryResults = [];
  try {
    const dynamodb = await createAwsClient(DynamoDBClient);

    const input = {
      TableName: QUESTION_ANSWERS_TABLE_NAME,
      KeyConditionExpression: "id = :queryUuid",
      ExpressionAttributeValues: {
        ":queryUuid": {
          S: queryUuid,
        },
      },
      ConsistentRead: true,
    };

    console.log("------- Get Query Results -------");
    console.log(input);

    const command = new QueryCommand(input);
    const response = await dynamodb.send(command);

    if (response.hasOwnProperty("Items")) {
      for (let i = 0; i < response.Items.length; i++) {
        queryResults.push({
          query: response.Items[i].sql_query.S,
          query_results: JSON.parse(response.Items[i].data.S).result,
          query_description: response.Items[i].sql_query_description.S,
        });
      }
    }

    return queryResults;
  } catch (error) {
    console.warn("Could not fetch query results from DynamoDB:", error.message);
    // Return empty array - this feature isn't used by the backend anyway
    return [];
  }
};

/**
 * Generates a chart based on answer and data
 * @param {Object} answer - Answer object containing text
 * @returns {Object} Chart configuration or rationale for no chart
 */
export const generateChart = async (
  answer
) => {
  const bedrock = await createAwsClient(BedrockRuntimeClient, { region: 'us-east-1' });
  let query_results = "";
  for (let i = 0; i < answer.queryResults.length; i++) {
    query_results += JSON.stringify(answer.queryResults[i].query_results) + "\n";
  }

  // Prepare the prompt
  let new_chart_prompt = CHART_PROMPT.replace(
    /<<answer>>/i,
    answer.text
  ).replace(/<<data_sources>>/i, query_results);

  const payload = {
    anthropic_version: "bedrock-2023-05-31",
    max_tokens: 2000,
    temperature: 0.3, 
    messages: [
      {
        role: "user",
        content: [{ type: "text", text: new_chart_prompt }],
      },
    ],
  };

  try {
    // Send the request to Bedrock
    console.log("------- Request chart -------");
    console.log(payload);

    const command = new InvokeModelCommand({
      contentType: "application/json",
      body: JSON.stringify(payload),
      modelId: MODEL_ID_FOR_CHART,
    });

    const apiResponse = await bedrock.send(command);
    const decodedResponseBody = new TextDecoder().decode(apiResponse.body);
    const responseBody = JSON.parse(decodedResponseBody).content[0].text;

    console.log("------- Response chart generation -------");
    console.log(responseBody);

    // Process the response
    const has_chart = parseInt(extractBetweenTags(responseBody, "has_chart"));

    if (has_chart) {
      // Extract raw chart configuration text
      const rawChartConfigText = extractBetweenTags(responseBody, "chart_configuration");
      let chartConfig;
      try {
        // First attempt: parse as-is (best case)
        chartConfig = JSON.parse(rawChartConfigText);
      } catch (err) {
        // Fallback: the model sometimes returns JS functions unquoted which breaks JSON.parse.
        // Convert unquoted function values into strings so handleFormatter can later convert them
        try {
          let sanitized = rawChartConfigText;

          // Wrap any property value that is a function literal into a quoted string.
          // Example: "formatter": function(val) { return "$" + val; }
          // becomes: "formatter": "function(val) { return \"$\" + val; }"
          sanitized = sanitized.replace(/("[^"]+"\s*:\s*)function\s*\([^)]*\)\s*\{[\s\S]*?\}/g, (match, p1) => {
            const fnText = match.replace(p1, '').trim();
            // Escape backslashes, double quotes and newlines for safe JSON string
            const esc = fnText.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n');
            return `${p1}\"${esc}\"`;
          });

          // Remove trailing commas before closing objects/arrays which may also break parsing
          sanitized = sanitized.replace(/,\s*([}\]])/g, '$1');

          chartConfig = JSON.parse(sanitized);
        } catch (err2) {
          console.error('Failed to parse sanitized chart config', err2);
          throw err2;
        }
      }
      
      // Apply consistency processing and validation
  const processedChartConfig = postProcessChart(handleFormatter(chartConfig));
      
      // Validate consistency (for logging/debugging)
      const validation = validateChartConsistency(processedChartConfig);
      if (!validation.isValid) {
        console.warn('Chart consistency issues found:', validation.issues);
      }
      
      const chart = {
        chart_type: removeCharFromStartAndEnd(
          extractBetweenTags(responseBody, "chart_type"),
          "\n"
        ),
        chart_configuration: processedChartConfig,
        caption: removeCharFromStartAndEnd(
          extractBetweenTags(responseBody, "caption"),
          "\n"
        ),
      };

      console.log("------- Final chart generation -------");
      console.log(chart);
      
      return chart;
    } else {
      return {
        rationale: removeCharFromStartAndEnd(
          extractBetweenTags(responseBody, "rationale"),
          "\n"
        ),
      };
    }
  } catch (error) {
    console.error("Chart generation failed:", error);
    return {
      rationale: "Error generating or parsing chart data.",
    };
  }
};

/**
 * Generates recommended prompts based on the AI's previous answer
 * Now calls backend API instead of Bedrock directly
 * @param {Object} answer - The AI's answer object containing text and context
 * @param {string} userQuery - The original user query
 * @returns {Array} Array of recommended prompts
 */
export const generateRecommendedPrompts = async (answer, userQuery) => {
  try {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://shipa-chatbot.cirrusgo.me';
    
    console.log("------- Calling backend for recommended prompts -------");
    console.log("Backend URL:", backendUrl);
    console.log("Answer text length:", answer?.text?.length);
    console.log("User query:", userQuery);
    
    const response = await fetch(`${backendUrl}/generate-prompts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        answer: answer.text,
        userQuery: userQuery
      })
    });

    console.log("Response status:", response.status);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error("Backend error response:", errorText);
      throw new Error(`Backend returned ${response.status}: ${errorText}`);
    }

    const data = await response.json();
    console.log("------- Backend prompts response -------");
    console.log("Full response:", data);
    console.log("Prompts array:", data.prompts);

    return data.prompts || [];
  } catch (error) {
    console.error("Recommended prompts generation failed:", error);
    return [];
  }
};



